import { useEffect, useRef } from 'react';

import './CubesField.css';

/**
 * Campo de cubos que se inclinam na direção do ponteiro.
 *
 * Ocupa o painel das telas de credencial. É decorativo — `aria-hidden`, e nada
 * do que ele faz altera o que a pessoa consegue ou não fazer na tela.
 *
 * **Sem biblioteca de animação.** A referência (reactbits) usa GSAP para
 * interpolar o giro de cada cubo; aqui o giro é interpolado à mão no laço de
 * quadro, em três linhas. Carregar 70 KB de biblioteca para decorar a tela de
 * entrada — a única que todo mundo abre, muitas vezes em 4G no meio do mato —
 * não se paga.
 *
 * Três cuidados que o efeito exige para não virar um problema:
 *
 * - **`prefers-reduced-motion` desliga o laço**, não só as transições. Zerar a
 *   duração de uma transição não para um `requestAnimationFrame`.
 * - **Aba escondida para o laço.** Uma tela de login esquecida aberta não pode
 *   ficar queimando processador a 60 quadros por segundo.
 * - **Escrita direta no DOM.** Estado de React por cubo re-renderizaria 64
 *   componentes por quadro. O giro é estilo inline escrito na referência
 *   guardada, que é o que esse tipo de animação pede.
 */

/** Lado da grade. 10×10 = 100 cubos: densidade da referência, sem pesar. */
const GRID = 10;

/** Inclinação máxima, no centro do foco. */
const MAX_ANGLE = 42;

/** Raio de influência, em células. Fora dele o cubo volta ao repouso. */
const RADIUS = 3.2;

/*
 * A ida é mais rápida que a volta, como na referência: o cubo reage na hora ao
 * ponteiro e relaxa devagar depois que ele passa. Fatores por quadro, a 60 Hz.
 */
const APPROACH = 0.18;
const RETURN = 0.07;

/** Passo do ponteiro imaginário quando ninguém está mexendo. */
const IDLE_SPEED = 0.018;

/** Tempo sem movimento até o ponteiro imaginário reassumir. */
const IDLE_AFTER_MS = 2500;

/** Atraso entre um anel e o próximo, na ondulação do clique. */
const RIPPLE_STEP_MS = 55;

/** Quanto a cor da ondulação fica de pé antes de voltar. */
const RIPPLE_HOLD_MS = 260;

export function CubesField({ className }: { className?: string | undefined }) {
  const sceneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const cubes = Array.from(scene.querySelectorAll<HTMLDivElement>('.cubes-field__cube'));
    if (cubes.length === 0) return;

    const menosMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');

    /** Giro atual de cada cubo, em graus. Índice = linha * GRID + coluna. */
    const angulos = new Float32Array(cubes.length);

    /** Onde o campo está olhando, em coordenadas de célula. */
    const foco = { x: GRID / 2, y: GRID / 2 };

    /** Alvo do ponteiro imaginário, e se o de verdade tem a palavra. */
    const alvo = { x: Math.random() * GRID, y: Math.random() * GRID };
    let ponteiroAtivo = false;
    let ocioso: ReturnType<typeof setTimeout> | null = null;
    let quadro: number | null = null;

    function desenhar(): void {
      for (let linha = 0; linha < GRID; linha += 1) {
        for (let coluna = 0; coluna < GRID; coluna += 1) {
          const indice = linha * GRID + coluna;
          const cubo = cubes[indice];
          if (!cubo) continue;

          const distancia = Math.hypot(linha - foco.y, coluna - foco.x);
          const desejado = distancia <= RADIUS ? (1 - distancia / RADIUS) * MAX_ANGLE : 0;

          const atual = angulos[indice] ?? 0;
          const passo = desejado > atual ? APPROACH : RETURN;
          const proximo = atual + (desejado - atual) * passo;
          angulos[indice] = proximo;

          /*
           * Abaixo de cinco centésimos de grau ninguém enxerga diferença, e
           * escrever mesmo assim custa 64 recálculos de estilo por quadro à toa.
           */
          if (Math.abs(proximo - atual) < 0.05 && Math.abs(proximo) < 0.05) continue;

          const graus = proximo.toFixed(2);
          cubo.style.transform = `rotateX(-${graus}deg) rotateY(${graus}deg)`;
        }
      }
    }

    function laco(): void {
      if (!ponteiroAtivo) {
        // Ponteiro imaginário vagando: aproxima do alvo e sorteia outro ao chegar.
        foco.x += (alvo.x - foco.x) * IDLE_SPEED;
        foco.y += (alvo.y - foco.y) * IDLE_SPEED;

        if (Math.hypot(alvo.x - foco.x, alvo.y - foco.y) < 0.15) {
          alvo.x = Math.random() * GRID;
          alvo.y = Math.random() * GRID;
        }
      }

      desenhar();
      quadro = requestAnimationFrame(laco);
    }

    function comecar(): void {
      if (quadro !== null || menosMovimento.matches || document.hidden) return;
      quadro = requestAnimationFrame(laco);
    }

    function parar(): void {
      if (quadro === null) return;
      cancelAnimationFrame(quadro);
      quadro = null;
    }

    function aoMover(evento: PointerEvent): void {
      const area = scene!.getBoundingClientRect();
      if (area.width === 0 || area.height === 0) return;

      foco.x = ((evento.clientX - area.left) / area.width) * GRID;
      foco.y = ((evento.clientY - area.top) / area.height) * GRID;

      ponteiroAtivo = true;
      if (ocioso) clearTimeout(ocioso);
      ocioso = setTimeout(() => {
        ponteiroAtivo = false;
        // O ponteiro imaginário reassume de onde o de verdade parou.
        alvo.x = Math.random() * GRID;
        alvo.y = Math.random() * GRID;
      }, IDLE_AFTER_MS);
    }

    function aoSair(): void {
      ponteiroAtivo = false;
      if (ocioso) clearTimeout(ocioso);
    }

    /** Ondulação a partir do ponto tocado, um anel de cada vez. */
    const voltas: ReturnType<typeof setTimeout>[] = [];

    function aoTocar(evento: PointerEvent): void {
      if (menosMovimento.matches) return;

      const area = scene!.getBoundingClientRect();
      if (area.width === 0 || area.height === 0) return;

      const colunaAlvo = Math.floor(((evento.clientX - area.left) / area.width) * GRID);
      const linhaAlvo = Math.floor(((evento.clientY - area.top) / area.height) * GRID);

      for (let linha = 0; linha < GRID; linha += 1) {
        for (let coluna = 0; coluna < GRID; coluna += 1) {
          const cubo = cubes[linha * GRID + coluna];
          if (!cubo) continue;

          const anel = Math.round(Math.hypot(linha - linhaAlvo, coluna - colunaAlvo));
          const atraso = anel * RIPPLE_STEP_MS;

          /*
           * Uma escrita por cubo, e não seis por face: as faces leem
           * `--cube-face` do cubo, e mudar a variável no pai faz a cor calculada
           * de cada face mudar — que é o que dispara a transição delas.
           */
          cubo.style.setProperty('--cube-delay', `${atraso}ms`);
          cubo.style.setProperty('--cube-face', 'var(--cube-face-ripple)');
          cubo.style.setProperty('--cube-edge', 'var(--cube-edge-ripple)');

          voltas.push(
            setTimeout(() => {
              cubo.style.setProperty('--cube-delay', '0ms');
              cubo.style.removeProperty('--cube-face');
              cubo.style.removeProperty('--cube-edge');
            }, atraso + RIPPLE_HOLD_MS),
          );
        }
      }
    }

    function aoTrocarVisibilidade(): void {
      if (document.hidden) parar();
      else comecar();
    }

    function aoTrocarPreferencia(): void {
      if (menosMovimento.matches) {
        parar();
        for (const cubo of cubes) cubo.style.transform = '';
      } else {
        comecar();
      }
    }

    scene.addEventListener('pointermove', aoMover);
    scene.addEventListener('pointerleave', aoSair);
    scene.addEventListener('pointerdown', aoTocar);
    document.addEventListener('visibilitychange', aoTrocarVisibilidade);
    menosMovimento.addEventListener('change', aoTrocarPreferencia);

    comecar();

    return () => {
      parar();
      if (ocioso) clearTimeout(ocioso);
      for (const volta of voltas) clearTimeout(volta);

      scene.removeEventListener('pointermove', aoMover);
      scene.removeEventListener('pointerleave', aoSair);
      scene.removeEventListener('pointerdown', aoTocar);
      document.removeEventListener('visibilitychange', aoTrocarVisibilidade);
      menosMovimento.removeEventListener('change', aoTrocarPreferencia);
    };
  }, []);

  const celulas = Array.from({ length: GRID * GRID }, (_, indice) => indice);

  return (
    <div className={className ? `cubes-field ${className}` : 'cubes-field'} aria-hidden>
      <div
        ref={sceneRef}
        className="cubes-field__scene"
        style={{
          gridTemplateColumns: `repeat(${GRID}, 1fr)`,
          gridTemplateRows: `repeat(${GRID}, 1fr)`,
          /*
           * Quadrado do lado da maior dimensão do painel, um pouco maior que
           * ele: os cubos das bordas saem do enquadramento e o campo lê como
           * superfície contínua, não como um widget colado no meio.
           *
           * `aspect-ratio` mantém o quadrado; os dois mínimos obrigam o lado a
           * cobrir tanto a largura quanto a altura, seja o painel largo ou alto.
           */
          aspectRatio: '1 / 1',
          minWidth: '112%',
          minHeight: '112%',
          gap: '5%',
        }}
      >
        {celulas.map((indice) => (
          <div key={indice} className="cubes-field__cube">
            <span className="cubes-field__face cubes-field__face--top" />
            <span className="cubes-field__face cubes-field__face--bottom" />
            <span className="cubes-field__face cubes-field__face--left" />
            <span className="cubes-field__face cubes-field__face--right" />
            <span className="cubes-field__face cubes-field__face--front" />
          </div>
        ))}
      </div>
    </div>
  );
}
